import React, { useGlobal } from 'reactn'
import { useNavigate, useParams } from 'react-router-dom'
import MedReservations from 'ui/pages/med/Reservations'
import { useEffect, useState } from 'react'
import {
  BankTransfer,
  Billing,
  BillingForm,
  ReservationDetailProps,
  ReservationListItem,
} from 'types/med'
import type { CallMethodVideoAppNames, ImageProps } from 'types/common'
import { jstNewDate } from 'ui/utils/jstDatetime'

import { formatDateString } from 'ui/utils/dateFormatUtils'
import {
  AttachmentImageNameProps,
  getAllImages,
  getSaluGiUserAccessCode,
} from 'app/personal/fetch'
import {
  getBankTransferSettings,
  getBilling,
  getDepartmentList,
  getReservationDetail,
  getReservationList,
  getVcUrl,
  postBilling,
  postReservationsCall,
  postVideoAppConnections,
  putReservationCancel,
  resendReservationMail,
  updateApplicantAddress,
  updateReservationDatetime,
} from './fetch'
import { sideBarRoute } from './utils'
import { routePaths } from 'app/routes'
import GaEvents from '../shared/GaEvents'
import {
  Api401Error,
  ApiErrorWithMessage,
  ApiErrorWithNoMessage,
  NotFoundError,
  OfflineError,
} from '../client'
import { originUrl } from 'config/url'
import { ChangeAddressFormProps } from 'ui/partials/ChangeAddressModalContent'
import { connectOAuth, OAuthResponse } from './OAuth'
import usePopupBlockModal from 'ui/hooks/usePopupBlockModal'
import useZoomReconnectionModal from 'ui/hooks/useZoomReconnectionModal'
import useZoomTemporaryErrorModal from 'ui/hooks/useZoomTemporaryErrorModal'

let lastLoadReservationId: number | null = null

const Reservations: React.FC = () => {
  const { date, reservation_id } = useParams<'date' | 'reservation_id'>()
  const navigate = useNavigate()

  const [user] = useGlobal('user')
  const [appClientId] = useGlobal('appClientId')
  const [update] = useGlobal('updateInformation')
  const [offline] = useGlobal('offline')
  const [dateReservationList, setDateReservationList] = useGlobal(
    'dateReservationList'
  )
  const reservationList = date ? dateReservationList?.[date] : []
  const [departmentListItems, setDepartmentListItems] = useGlobal(
    'departmentListItems'
  )
  const [reservation, setReservation] = useState<ReservationDetailProps | null>(
    null
  )
  const [reservationId, setReservationId] = useState<number | null>(null)

  const { showPopupBlockModal, popupBlockModalComponent } = usePopupBlockModal()

  const {
    zoomReconnectionStart,
    zoomReconnectionSuccess,
    zoomReconnectionModalComponent,
  } = useZoomReconnectionModal({
    onPopupBlock: () => showPopupBlockModal(),
    onConnectOAuth: () => connectOAuth('zoom', appClientId?.zoom),
  })

  const {
    openZoomTemporaryErrorModal,
    closeZoomTemporaryErrorModal,
    zoomTemporaryErrorModalComponent,
  } = useZoomTemporaryErrorModal({
    onReconnect: () => {
      // NOTE: モーダルを開くとタイマーで再接続する zoomのみのエラーなので引数は固定
      vcStartHandler('video_zoom')
    },
  })

  const [bankTransferSettings, setBankTransferSettings] = useState<
    BankTransfer | undefined
  >()

  const [billings, setBillings] = useState<Billing[]>()

  const [reloadTrigger, setReloadTrigger] = useState(0) // NOTE: useEffectを走らせるための更新カウンタ

  useEffect(() => {
    if (!user) return
    if (reservationId) {
      lastLoadReservationId = reservationId
      getReservationDetail({
        reservation_id: reservationId,
      })
        .then((data) => {
          // NOTE: 最後のリクエストidに対するレスポンスでなければ破棄する
          if (lastLoadReservationId !== reservationId) return

          setReservation(data)
          /**
           * NOTE: 添付画像があれば全て取得してセット
           */
          const names: AttachmentImageNameProps[] = [
            'insurance_card_images',
            'certificate_images',
            'identity_verification_images',
            'attachment_images',
          ]
          return getAllImages({
            names,
            reservation_id: reservationId,
            access_key: data.access_key,
          }).then((results) => {
            results.forEach((images: ImageProps[], index) => {
              // NOTE: 最後のリクエストidに対するレスポンスでなければ破棄する
              if (lastLoadReservationId !== reservationId) return
              if (images.length) {
                const name = names[index]
                setReservation((prev) => prev && { ...prev, [name]: images })
              }
            })
          })
        })
        .catch((e) => {
          if (e instanceof NotFoundError) {
            return
          }
          if (e instanceof Api401Error) {
            return
          }
          console.log(e)
          navigate(routePaths.error)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, reservationId, reloadTrigger])

  // NOTE: ZoomのOAuth成功
  const receiveMessage = (event: MessageEvent) => {
    if (event.origin !== originUrl) {
      return
    }
    if (event.data?.connect_zoom) {
      const connectZoom: OAuthResponse = event.data.connect_zoom
      if (connectZoom?.error) {
        console.log(connectZoom?.error)
        navigate(routePaths.error)
        return
      }
      postVideoAppConnections({
        app: 'video_zoom',
        department_id: Number(reservation?.reservation_department_id),
        data: {
          code: connectZoom?.code,
        },
      })
        .then(() => zoomReconnectionSuccess())
        .catch((e) => {
          console.log(e)
          navigate(routePaths.error)
        })
      return
    }
  }

  useEffect(() => {
    if (!user) return
    getBankTransferSettings().then(setBankTransferSettings).catch(console.log)
    getDepartmentList().then(setDepartmentListItems)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!reservation) return
    window.addEventListener('message', receiveMessage, false)
    return () => {
      window.removeEventListener('message', receiveMessage)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation])

  const fetchReservationList = (date: string) => {
    return getReservationList({
      reservation_date: date,
      offset: 0, //TODO: ページング実装はphase2
      limit: 1000, //TODO: ページング実装はphase2
    })
      .then((list: ReservationListItem[]) =>
        setDateReservationList({
          ...dateReservationList,
          [date]: list,
        })
      )
      .catch((e) => {
        if (e instanceof ApiErrorWithNoMessage) {
          // NOTE: 予期しないエラー
          navigate(routePaths.error)
        }
        return
      })
  }

  useEffect(() => {
    // NOTE: dateがなければ予約リストを空にして遷移
    if (!user) return
    // NOTE: 日付、予約選択を変更した場合にその日付のリストを再取得
    if (!date) {
      setDateReservationList({})
      navigate(
        `${routePaths.medical}${routePaths.reservations}/${formatDateString(
          jstNewDate
        )}`
      )
      return
    }
    fetchReservationList(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, reservation_id])

  useEffect(() => {
    if (
      !date ||
      !dateReservationList ||
      Object.keys(dateReservationList).length === 0
    ) {
      return
    }
    if (!reservation_id) {
      // NOTE: pathにreservation_idがない場合
      // NOTE: リスト要素があれば先頭を選択
      if (reservationList?.length) {
        setReservationId(reservationList[0].id)
      } else {
        // NOTE: リストが空なら選択解除
        setReservation(null)
        setReservationId(null)
      }
    } else {
      setReservationId(Number(reservation_id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation_id, dateReservationList, date])

  // NOTE: reloadTriggerの変更でリストを再読み込み
  useEffect(() => {
    if (!user) return
    date && reloadTrigger > 0 && fetchReservationList(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTrigger])

  useEffect(() => {
    if (!user) return
    if (!reservationId) return
    getBilling(reservationId)
      .then(setBillings)
      .catch((e) => {
        if (e instanceof NotFoundError) {
          setBillings([])
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationId, reloadTrigger])

  const onChangeDateHandler = (date: Date) => {
    navigate(
      `${routePaths.medical}${routePaths.reservations}/${formatDateString(
        date
      )}`
    )
  }
  const onSelectReservationHandler = (reservation: ReservationListItem) => {
    navigate(
      `${routePaths.medical}${routePaths.reservations}/${date}/${reservation.id}`
    )
  }

  const onChangeAddressHandler = (data: ChangeAddressFormProps) => {
    return reservationId
      ? updateApplicantAddress(reservationId)(data).then(() =>
          setReloadTrigger((prev) => prev + 1)
        )
      : Promise.reject()
  }

  const onClickPhrIntegrationButton = () => {
    if (!reservationId) return Promise.reject()
    return getSaluGiUserAccessCode(reservationId).then(({ access_code }) => ({
      token: access_code,
    }))
  }

  const vcStartHandler = (app: CallMethodVideoAppNames) => {
    if (!user || !reservation) return undefined

    // NOTE: zoom503再接続時にモーダルが開いていた時は閉じる
    closeZoomTemporaryErrorModal()

    return getVcUrl(app, reservation.id)
      .then(({ url }) => {
        const name = window.open(url, `${app}_start`)
        if (name === null) {
          showPopupBlockModal()
        } else {
          GaEvents.medicalVideoChatStart({
            med: user,
            reservation,
            type: app.replace('video_', ''),
          })
        }
      })
      .catch((e) => {
        if (e instanceof NotFoundError) {
          // TODO: 時計のずれによる開始前状態につき無視
          return
        }
        if (
          app === 'video_zoom' &&
          e instanceof ApiErrorWithMessage &&
          e.has({
            type: 'server_error',
            key: 'token',
            value: 'valid',
          })
        ) {
          zoomReconnectionStart()
          return
        }
        // Zoomのサーバーの名前解決ができないなど一時的なサーバーエラー
        if (app === 'video_zoom' && e.response.status === 503) {
          openZoomTemporaryErrorModal()
          return
        }
        // その他の予期しないエラー
        console.log(e)
        navigate(routePaths.error)
      })
  }

  const submitBillingHandler: (data: BillingForm) => Promise<void> = (data) => {
    if (reservationId == null) {
      throw new NotFoundError()
    }
    if (offline) {
      throw new OfflineError()
    }
    return postBilling({ ...data, id: reservationId }).then(() =>
      setReloadTrigger((prev) => prev + 1)
    )
  }

  const checkMeetingFinishedHandler = (result: boolean) => {
    if (
      reservation == null ||
      reservationId === null ||
      reservation?.status === 'canceled'
    )
      return
    postReservationsCall(reservationId, result)
      .then(
        () => setReloadTrigger((prev) => prev + 1) // 再読み込みさせる
      )
      .catch(() => navigate(routePaths.error))
  }

  const submitCancelHandler = () => {
    if (reservation == null || reservationId === null) return Promise.reject()
    return putReservationCancel(reservationId).then(() =>
      setReloadTrigger((prev) => prev + 1)
    )
  }

  const changeReservationHandler = (data: {
    reservation_datetime_start: string
    reservation_datetime_end: string
  }) => {
    if (reservationId === null || reservation === null) return Promise.reject()
    return updateReservationDatetime(reservationId, data)
      .then(() => {
        // NOTE: 更新時URLにreservation_idがない時はreservation_idのあるURLにリダイレクト
        if (!reservation_id) {
          const dateString = data.reservation_datetime_start.substr(0, 10)
          navigate(
            `${routePaths.medical}${routePaths.reservations}/${dateString}/${reservationId}`
          )
        }
      })
      .finally(() => {
        setReloadTrigger((prev) => prev + 1)
      })
  }

  // NOTE: アクセスされたURLにreservation_idがある時dateとreservationの日付に食い違いがあった時はリダイレクト
  useEffect(() => {
    if (date && reservation_id && reservation) {
      const dateString = reservation.reservation_datetime_start.substr(0, 10)
      if (
        Number(reservation.id) === Number(reservation_id) &&
        date !== dateString
      ) {
        navigate(
          `${routePaths.medical}${routePaths.reservations}/${dateString}/${reservation_id}`,
          { replace: true }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, reservation])

  if (!user) return null

  return (
    <>
      <MedReservations
        onChangeReservationDatetime={changeReservationHandler}
        med={user}
        update={update}
        selectRoute={{
          route: 'reservations',
          onSelect: sideBarRoute(navigate)('reservations'),
        }}
        reservation={reservation}
        reservationSubmenu={{
          reservations: reservationList,
          onChangeDate: onChangeDateHandler,
          onClickItem: onSelectReservationHandler,
          selectedId: reservationId,
        }}
        editable={false}
        onSubmitCancel={submitCancelHandler}
        onReSendReservationMail={() =>
          resendReservationMail(Number(reservationId))
        }
        onStartVideoMeeting={vcStartHandler}
        onCheckMeetingFinished={checkMeetingFinishedHandler}
        billing={{
          billings: billings,
          bankTransfer: bankTransferSettings,
        }}
        onSubmitBilling={submitBillingHandler}
        shipping={{
          onChangeAddress: onChangeAddressHandler,
        }}
        phrIntegration={{
          onClickPhrIntegrationButton,
        }}
        departmentSetting={departmentListItems?.find(
          ({ id }) => id === reservation?.reservation_department_id
        )}
      />
      {popupBlockModalComponent}
      {zoomReconnectionModalComponent}
      {zoomTemporaryErrorModalComponent}
    </>
  )
}

export default Reservations
